import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, ImageIcon, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { web } from "../../lib/api";
import { Btn } from "./kit";
import { ProspectCard } from "./ProspectCard";

export function ProspectCardButton({ data }) {
  const { me } = useAuth();
  const brand = useApi("/branding", web);
  const ref = useRef(null);
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const name = data?.player?.name ?? "prospect";

  const make = async () => {
    setBusy(true);
    try {
      await document.fonts.ready;
      const url = await toPng(ref.current, { pixelRatio: 1, cacheBust: true, backgroundColor: "#0A0E1A" });
      setImg(url);
    } catch {
      toast.error("Couldn't build the card. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const fileName = `${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-gameday-card.png`;
  const reel = (Array.isArray(data?.highlight_links) ? data.highlight_links : Array.isArray(data?.player?.highlight_links) ? data.player.highlight_links : [])
    .map((l) => (typeof l === "string" ? l : l?.url)).find(Boolean);
  const copyReel = async () => {
    try { await navigator.clipboard.writeText(reel); toast.success("Highlights link copied — paste it with the card"); }
    catch { toast.error("Couldn't copy the link"); }
  };

  return (
    <>
      <Btn variant="ghost" data-testid="prospect-card-button" onClick={make} disabled={busy || brand.loading}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}Prospect card
      </Btn>
      <div aria-hidden className="pointer-events-none fixed left-[-20000px] top-0">
        <ProspectCard ref={ref} data={data} org={me?.org} brand={brand.data} />
      </div>
      <Dialog open={!!img} onOpenChange={(o) => { if (!o) setImg(null); }}>
        <DialogContent className="max-w-lg border-[#1E293B] bg-[#0F172A] text-white" data-testid="prospect-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Prospect card</DialogTitle>
            <DialogDescription className="text-slate-400">A 1080×1350 image, ready to drop into a staff group chat.</DialogDescription>
          </DialogHeader>
          {img && <img src={img} alt={`${name} prospect card`} data-testid="prospect-card-preview" className="w-full rounded-xl border border-[#1E293B]" />}
          <a href={img ?? "#"} download={fileName} data-testid="prospect-card-download" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#39FF14] px-5 py-3 text-sm font-bold text-[#0A0E1A] hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]">
            <Download className="h-4 w-4" />Download PNG
          </a>
          {reel && (
            <button data-testid="prospect-card-copy-highlights" onClick={copyReel} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#39FF14]/40 px-5 py-3 text-sm font-semibold text-[#39FF14] transition-colors hover:bg-[#39FF14]/10">
              <Link2 className="h-4 w-4" />Copy highlights link
            </button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
